import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, ArrowRight, Loader2, MessageCircle, Twitter } from "lucide-react";
import { toast } from "sonner";
import { PERSONA_META, DOMAIN_ORDER, useLang } from "@/components/landing/i18n";

export default function ResultCard() {
  const { id } = useParams();
  const { lang } = useLang();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/results/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(setR)
      .catch(() => setErr(true));
  }, [id]);

  const persona = r ? PERSONA_META[r.top] : null;
  const ptxt = persona ? persona[lang] || persona.en : null;
  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareText = r && ptxt ? `I got "${ptxt.name}" on the Bright IQ psychometric quiz — Know yourself. Shape your tomorrow.` : "";
  const maxScore = r ? Math.max(1, ...Object.values(r.scores || {})) : 1;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen bg-ink text-paper font-body flex flex-col items-center justify-center px-5 py-16 relative overflow-hidden" data-testid="result-page">
      <div className="absolute inset-0 spotlight-glow pointer-events-none" aria-hidden />
      {err && (
        <div className="relative text-center" data-testid="result-error">
          <h1 className="font-display font-bold text-3xl">Result not found</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-iqcyan font-semibold">Take the quiz yourself <ArrowRight className="h-4 w-4" /></Link>
        </div>
      )}
      {!r && !err && <Loader2 className="h-8 w-8 animate-spin text-iqcyan relative" data-testid="result-loading" />}
      {r && persona && (
        <motion.div initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="relative w-full max-w-md">
          <div className="rounded-[2rem] bg-cream text-ink p-8 sm:p-10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.6)]" data-testid="result-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
                <span className="font-display font-bold tracking-tight">Bright<span className="text-iqblue">IQ</span></span>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-ink/40">{r.kind === "full" ? (r.track === "professional" ? "Professional battery" : "Student battery") : "First insight"}</span>
                {r.industry && <span className="text-[10px] font-semibold tracking-wide text-iqpurple" data-testid="result-industry">{r.industry}</span>}
              </div>
            </div>
            <div className="mt-8 text-xs font-semibold tracking-[0.25em] uppercase text-ink/40">{r.name.split(" ")[0]} is</div>
            <h1 data-testid="result-persona" className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-2" style={{ color: persona.color }}>
              {ptxt.name}
            </h1>
            <p className="mt-4 text-sm text-ink/65 leading-relaxed">{ptxt.blurb}</p>
            {typeof r.aptPct === "number" && (
              <div className="mt-5 inline-flex items-center rounded-full bg-ink text-paper px-4 py-1.5 text-xs font-semibold" data-testid="result-apt">Aptitude score: {r.aptPct}%</div>
            )}
            <div className="mt-7 space-y-2">
              {DOMAIN_ORDER.map((key) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="w-24 text-[10px] font-semibold text-ink/55 text-right truncate">{PERSONA_META[key][lang]?.name || PERSONA_META[key].en.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-ink/8 overflow-hidden">
                    <motion.div className="h-full rounded-full" style={{ background: PERSONA_META[key].color }} initial={{ width: 0 }} animate={{ width: `${((r.scores?.[key] || 0) / maxScore) * 100}%` }} transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-7 pt-5 border-t border-ink/10 flex items-center justify-between text-[11px] text-ink/45">
              <span>{new Date(r.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
              <span className="font-serifa italic">Know yourself. Shape your tomorrow.</span>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-3">
            <a href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className="flex items-center gap-2 bg-[#25D366] text-white font-semibold text-sm px-5 py-3 rounded-full hover:brightness-110 transition-all">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="flex items-center gap-2 bg-white/10 border border-white/20 font-semibold text-sm px-5 py-3 rounded-full hover:bg-white/20 transition-all">
              <Twitter className="h-4 w-4" /> Post
            </a>
            <button onClick={copy} data-testid="share-copy" className="flex items-center gap-2 bg-white/10 border border-white/20 font-semibold text-sm px-5 py-3 rounded-full hover:bg-white/20 transition-all">
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>
          <div className="mt-8 text-center">
            <Link to="/" data-testid="result-cta-link" className="inline-flex items-center gap-2 text-iqcyan font-semibold hover:gap-3 transition-all">
              Discover your own persona <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
}
